import { Text, View } from '@react-pdf/renderer';
import {
  RDateCity,
  RHeader,
  RPage,
  RViewer,
} from '../../../components/PdfRender';
import { useCompany } from '../../../hooks/company';
import { IDocument, IPrescriptionItem } from '../../interfaces';
import { ICompany } from '../../interfaces/company.interface';
import * as S from './styles';
import { Html } from 'react-pdf-html';
import { RSignature } from '../../../components/PdfRender/RSignature';
import { formatDate } from '../../utils/format-date';
import { MARITAL_STATUS_LIST } from '../../enums/marital_status.enum';
import { useAuth } from '../../../hooks/auth';

interface IDocumentReportProps {
  report: IDocument | null;
  signature?: string | null;
  validationCode?: string | null;
  companyOverride?: ICompany | null;
  clinicOverride?: string | null;
}

export const DocumentReport: React.FC<IDocumentReportProps> = ({
  report,
  signature,
  validationCode,
  companyOverride,
  clinicOverride,
}) => {
  const { company } = useCompany();
  const { clinic } = useAuth();
  const reportCompany = companyOverride ?? company;
  const reportClinic =
    clinicOverride ||
    clinic ||
    sessionStorage.getItem('@Sigeclin:patientTenant') ||
    '';

  const escapeHtml = (value?: string | null) =>
    (value || '')
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;');

  const parsePrescriptionItems = (): IPrescriptionItem[] => {
    if (!report?.prescription_items) {
      return [];
    }

    if (Array.isArray(report.prescription_items)) {
      return report.prescription_items as IPrescriptionItem[];
    }

    try {
      return JSON.parse(report.prescription_items) as IPrescriptionItem[];
    } catch (_error) {
      return [];
    }
  };

  let renderText: string | null = null;

  if (report) {
    const {
      client_name,
      cpf,
      birthday,
      cell_phone,
      gender,
      marital_status,
      email,
      naturalness,
      nationality,
      profession,
      address,
      address_number,
      address_complement,
      address_neighborhood,
      address_cep,
      address_city,
      address_state,
    } = report;

    const marital = MARITAL_STATUS_LIST.find((x) => x.value === marital_status);

    const originalText = `${report.text_description}`;
    const prescriptionItemsHtml = parsePrescriptionItems()
      .filter(
        (item: IPrescriptionItem) =>
          item?.medicine ||
          item?.dosage ||
          item?.frequency ||
          item?.duration ||
          item?.instructions,
      )
      .map(
        (item: IPrescriptionItem) =>
          `<li><strong>${escapeHtml(item.medicine)}</strong>${
            item.dosage ? ` - ${escapeHtml(item.dosage)}` : ''
          }${item.frequency ? `, ${escapeHtml(item.frequency)}` : ''}${
            item.duration ? `, por ${escapeHtml(item.duration)}` : ''
          }${
            item.instructions
              ? `<br/><span>${escapeHtml(item.instructions)}</span>`
              : ''
          }</li>`,
      )
      .join('');
    const prescriptionItemsSection = prescriptionItemsHtml
      ? `<div><strong>Medicamentos prescritos</strong><ol>${prescriptionItemsHtml}</ol></div>`
      : '';
    const prescriptionGuidelinesSection = report.prescription_guidelines
      ? `<div><strong>Orientações</strong><p>${escapeHtml(
          report.prescription_guidelines,
        )}</p></div>`
      : '';
    const prescriptionDateText = report.prescription_date
      ? formatDate(report.prescription_date)
      : report.created_at
        ? formatDate(report.created_at)
        : '';

    renderText = originalText
      .replace('{nome}', client_name?.toUpperCase() ?? '')
      .replace('{cpf}', cpf ?? '')
      .replace('{nascimento}', birthday ? formatDate(birthday) : '')
      .replace('{celular}', cell_phone ?? '')
      .replace(
        '{sexo}',
        gender ? (gender === 'f' ? 'Feminino' : 'Masculino') : '',
      )
      .replace('{estadoCivil}', marital ? marital.label : '')
      .replace('{email}', email ?? '')
      .replace('{naturalidade}', naturalness ?? '')
      .replace('{nacionalidade}', nationality ?? '')
      .replace('{profissao}', profession ?? '')
      .replace('{cep}', address_cep ?? '')
      .replace('{logradouro}', address ?? '')
      .replace('{numero}', address_number ?? '')
      .replace('{bairro}', address_neighborhood ?? '')
      .replace('{complemento}', address_complement ?? '')
      .replace('{cidade}', address_city ?? '')
      .replace('{uf}', address_state ?? '')
      .replace('{dataReceita}', prescriptionDateText)
      .replace('{itensReceita}', prescriptionItemsSection)
      .replace('{orientacoesReceita}', prescriptionGuidelinesSection);

    if (report.kind === 'PRESCRIPTION') {
      if (
        !originalText.includes('{itensReceita}') &&
        prescriptionItemsSection
      ) {
        renderText += prescriptionItemsSection;
      }

      if (
        !originalText.includes('{orientacoesReceita}') &&
        prescriptionGuidelinesSection
      ) {
        renderText += prescriptionGuidelinesSection;
      }
    }
  }

  return (
    <RViewer>
      <RPage>
        <View style={S.styles.container}>
          <RHeader company={reportCompany} />
          <View style={S.styles.containerCode}>
            <Text style={S.styles.codeText}>Nº {report?.code}</Text>
          </View>
          <View style={S.styles.containerTitleDescription}>
            <View style={S.styles.containerTitle}>
              <Text style={S.styles.titleText}>{report?.title_document}</Text>
            </View>
            <View style={S.styles.containerDescription}>
              <Html style={S.styles.descriptionText}>{`${renderText}`}</Html>
            </View>
          </View>
          <RDateCity company={reportCompany} issueDate={report?.created_at} />
          <RSignature
            professional_name={report?.professional_name}
            terminology_description={report?.terminology_description}
            terminology_code={report?.terminology_code}
            rqe={report?.rqe}
            signature={signature}
            signed_at={report?.signed_at}
            validation_code={
              validationCode || report?.signature_validation_code
            }
            clinic={reportClinic}
          />
        </View>
      </RPage>
    </RViewer>
  );
};
